import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { AuthService } from 'app/modules/auth/auth.service';
import { PERMISSION_VIOLATION_NOTICES_VIEW } from 'app/modules/violations/notices/violation-notice.service';
import { PERMISSION_VIOLATION_REPORTERS_VIEW } from 'app/modules/violations/violation-reporters/violation-reporters.service';
import { PERMISSION_VIOLATION_SEVERITIES_VIEW } from 'app/modules/violations/violation-severities/violation-severities.service';
import * as permissions from '../types/permissions.type';
import { PermissionService } from './permission.service';

@Injectable({
  providedIn: 'root',
})
export class SideMenuService {
  permissionService = inject(PermissionService);
  authService = inject(AuthService);

  menuItems: FuseNavigationItem[] = [
    // 1. Dashboard
    {
      id: 'dashboard',
      title: 'Dashboard',
      type: 'basic',
      icon: 'dashboard',
      link: '/dashboard',
    },
    // 2. Communities
    {
      id: 'associations',
      title: 'Communities',
      type: 'basic',
      icon: 'house',
      link: '/associations',
    },
    // 3. Services
    {
      id: 'eservices',
      title: 'Services',
      type: 'collapsable',
      icon: 'home_repair_service',
      hidden: () => {
        return this.permissionService.cannot(permissions.PERMISSION_ESERVICE_VIEW);
      },
      children: [
        {
          id: 'eservices-list',
          title: 'Requests',
          type: 'basic',
          link: '/eservices/list',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_ESERVICE_VIEW);
          },
        },
        {
          id: 'eservices-owner-kyc',
          title: 'Owner KYC',
          type: 'basic',
          link: '/eservices/owner-kyc',
          hidden: (item: FuseNavigationItem) => {
            return !this.authService.profile()?.company?.owner_dv_enabled || this.permissionService.cannot(permissions.PERMISSION_OWNER_DV_VIEW);
          },
        },
        {
          id: 'eservices-tanency',
          title: 'Tenancy Requests',
          type: 'basic',
          link: '/eservices/tanency-contract',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_CAN_VIEW_RESIDENT);
          },
        },
        {
          id: 'eservices-ownerships',
          title: 'Ownerships',
          type: 'basic',
          link: '/eservices/ownerships',
          hidden: (item: FuseNavigationItem) => {
            return !this.authService.profile()?.company?.is_mollak_enable || this.permissionService.cannot(permissions.PERMISSION_UNIT_OWNERS_MOLLAK_VIEW);
          },
        },
        {
          id: 'eservices-visitor-pass',
          title: 'Visitor Passes',
          type: 'basic',
          link: '/eservices/visitor-pass',
        },
      ],
    },
    // 4. Happiness Center
    {
      id: 'happiness-center',
      title: 'Happiness Center',
      type: 'collapsable',
      icon: 'sentiment_satisfied',
      hidden: this.hideGroup,
      children: [
        {
          id: 'online-ticket',
          title: 'Online Ticket',
          type: 'basic',
          link: '/happiness-center/online',
          hidden: () => {
            return this.permissionService.cannot(permissions.PERMISSION_HAPPINESS_CENTER_VIEW);
          },
        },
        {
          id: 'mollak-service-requests',
          title: 'Mollak Service Requests',
          type: 'basic',
          link: '/happiness-center/mollak-service-requests',
          hidden: () => {
            return this.permissionService.cannot(permissions.PERMISSION_MOLLAK_SERVICE_REQUEST_VIEW);
          },
        },
        {
          id: 'callcenter-tickets',
          title: 'Call Center Tickets',
          type: 'basic',
          link: '/happiness-center/call-center',
          hidden: () => {
            return !this.authService.profile()?.company?.call_center_settings?.is_call_center_enabled || this.permissionService.cannot(permissions.PERMISSION_CALL_CENTER_VIEW);
          },
        },
        {
          id: 'mail-box',
          title: 'Mail Box',
          type: 'basic',
          link: '/happiness-center/mail-box',
          hidden: () => {
            return !this.authService.profile()?.has_email_integration;
          },
        },
        {
          id: 'happiness-center-settings',
          title: 'Settings',
          type: 'basic',
          link: '/happiness-center/settings',
          hidden: () => {
            const canSeeAgents = !!this.authService.profile()?.company?.call_center_settings?.is_call_center_enabled && this.permissionService.can(permissions.PERMISSION_CALL_CENTER_AGENTS_VIEW);

            return !canSeeAgents && this.permissionService.cannot(permissions.PERMISSION_EMAIL_INTEGRATION_MANAGE);
          },
        },
      ],
    },
    // 5. Accounting
    {
      id: 'accounting',
      title: 'Accounting',
      type: 'collapsable',
      icon: 'calculate',
      hidden: this.hideGroup,
      children: [
        {
          id: 'accounts',
          title: 'Chart of Accounts',
          type: 'basic',
          link: '/accounting/accounts',
          hidden: () => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_CHART_VIEW, permissions.PERMISSION_ACCOUNTING_CHART_ADD_UPDATE, permissions.PERMISSION_ACCOUNTING_CHART_DELETE]);
          },
        },
        {
          id: 'GeneralLedger',
          title: 'General Ledger',
          type: 'basic',
          link: '/accounting/ledgers/gl',
          hidden: () => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_GENERAL_LEDGER, permissions.PERMISSION_ACCOUNTING_JOURNAL_ENTRY]);
          },
        },
        {
          id: 'JVS',
          title: 'JVs',
          type: 'basic',
          link: '/accounting/ledgers/jvs',
          hidden: () => {
            return this.permissionService.cannot(permissions.PERMISSION_CAN_VIEW_DRAFT_JV);
          },
        },
        {
          id: 'IncomingPayments',
          title: 'Incoming Payments',
          type: 'basic',
          link: '/accounting/online-payments',
          hidden: () => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_ONLINE_PAYMENTS_LIST, permissions.PERMISSION_ACCOUNTING_VERIFY_PROOF]);
          },
        },
        {
          id: 'Invoices',
          title: 'Invoices',
          type: 'basic',
          link: '/accounting/invoices',
          hidden: () => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_INVOICES_VIEW, permissions.PERMISSION_ACCOUNTING_INVOICES_ADD]);
          },
        },
        {
          id: 'receipts',
          title: 'Receipts',
          type: 'basic',
          link: '/accounting/receipts',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_RECEIPTS_VIEW, permissions.PERMISSION_ACCOUNTING_RECEIPTS_ADD]);
          },
        },
        {
          id: 'pdcs',
          title: 'PDCs',
          type: 'basic',
          link: '/accounting/pdcs',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_RECEIPTS_VIEW, permissions.PERMISSION_ACCOUNTING_RECEIPTS_ADD]);
          },
        },
        {
          id: 'refunds',
          title: 'Refunds',
          type: 'basic',
          link: '/accounting/refunds',
          hidden: () => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_REFUNDS_VIEW, permissions.PERMISSION_ACCOUNTING_REFUNDS_ADD]);
          },
        },
        {
          id: 'Security Deposits',
          title: 'Security Deposits',
          type: 'basic',
          link: '/accounting/security-deposits',
          hidden: () => {
            return !this.permissionService.canany([
              permissions.PERMISSION_ACCOUNTING_SECURITY_DEPOSITS_VIEW,
              permissions.PERMISSION_ACCOUNTING_SECURITY_DEPOSITS_ADD,
              permissions.PERMISSION_ACCOUNTING_SECURITY_DEPOSITS_REFUND,
            ]);
          },
        },
        {
          id: 'ISSUED CREDIT NOTES',
          title: 'Issued Credit Notes',
          type: 'basic',
          link: '/accounting/issued-credit-notes',
          hidden: () => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_ISSUED_CREDIT_NOTES_VIEW, permissions.PERMISSION_ACCOUNTING_ISSUED_CREDIT_NOTES_ADD]);
          },
        },
        {
          id: 'provisions',
          title: 'Provisions',
          type: 'basic',
          link: '/accounting/provisions',
          hidden: () => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_PROVISIONS_VIEW, permissions.PERMISSION_ACCOUNTING_PROVISIONS_ADD]);
          },
        },
        {
          id: 'bills',
          title: 'Bills',
          type: 'basic',
          link: '/accounting/bills',
          hidden: () => {
            return this.permissionService.cannot(permissions.PERMISSION_ACCOUNTING_BILLS_VIEW);
          },
        },
        {
          id: 'RECEIVED CREDIT NOTES',
          title: 'Received Credit Notes',
          type: 'basic',
          link: '/accounting/credit-notes',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_ACCOUNTING_CREDIT_NOTES_VIEW);
          },
        },
        {
          id: 'ADVANCE_PAYMENTS',
          title: 'Advance Payments',
          type: 'basic',
          link: '/accounting/advance-payments',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_ACCOUNTING_ADVANCE_PAYMENTS_VIEW);
          },
        },
        {
          id: 'penalties',
          title: 'Penalties',
          type: 'basic',
          link: '/accounting/penalties',
          hidden: () => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_PENALTIES_VIEW, permissions.PERMISSION_ACCOUNTING_PENALTIES_ADD]);
          },
        },
        {
          id: 'BUDGETS',
          title: 'Budgets',
          type: 'basic',
          link: '/accounting/budgets',
          hidden: () => {
            return !this.permissionService.canany([
              permissions.PERMISSION_ACCOUNTING_BUDGETS_VIEW,
              permissions.PERMISSION_ACCOUNTING_BUDGETS_ADD_UPDATE,
              permissions.PERMISSION_ACCOUNTING_BUDGETS_DELETE,
            ]);
          },
        },
        {
          id: 'pettyCash',
          title: 'Petty Cash',
          type: 'basic',
          link: '/accounting/petty-cash',
          hidden: () => {
            return !this.permissionService.canany([
              permissions.PERMISSION_ACCOUNTING_PETTY_CASH_VIEW_ALL,
              permissions.PERMISSION_ACCOUNTING_PETTY_CASH_WITHDRAW,
              permissions.PERMISSION_ACCOUNTING_PETTY_CASH_ISSUE_REFUND,
              permissions.PERMISSION_ACCOUNTING_PETTY_CASH_RECEIVE,
              permissions.PERMISSION_ACCOUNTING_PETTY_CASH_CLAIM_OTHER,
              permissions.PERMISSION_ACCOUNTING_PETTY_CASH_DELETE,
            ]);
          },
        },
        {
          id: 'book',
          title: 'Cash Book',
          type: 'basic',
          link: '/accounting/cash/book',
          hidden: () => {
            return !this.permissionService.canany([permissions.PERMISSION_ACCOUNTING_CASH_VIEW, permissions.PERMISSION_ACCOUNTING_CASH_DEPOSIT]);
          },
        },
        {
          id: 'BANKING',
          title: 'Banking',
          type: 'basic',
          link: '/accounting/banking/accounts',
          hidden: () => {
            return this.permissionService.cannot(permissions.PERMISSION_ACCOUNTING_BANKING_VIEW);
          },
        },
        {
          id: 'SUBSCRIPTION',
          title: 'Subscription',
          type: 'basic',
          link: '/accounting/subscription',
          hidden: (item: FuseNavigationItem) => {
            const isSubscribed = !!this.authService.profile()?.company?.bill_start;
            return !(this.permissionService.can(permissions.PERMISSION_ACCOUNTING_BILLS_VIEW) || (this.permissionService.can(permissions.PERMISSION_SUBSCRIPTION_BILLS_VIEW) && isSubscribed));
          },
        },
        {
          id: 'vat',
          title: 'VAT',
          type: 'basic',
          link: '/accounting/taxation',
          hidden: () => {
            return this.permissionService.cannot(permissions.PERMISSION_REPORT_TAXATION);
          },
        },
        {
          id: 'ACCOUNTING_SETTINGS',
          title: 'AI Settings',
          type: 'basic',
          link: '/accounting/settings/list',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([
              permissions.PERMISSION_ACCOUNTING_SETTINGS_RECEIVABLES_ACCOUNTS,
              permissions.PERMISSION_ACCOUNTING_SETTINGS_ASSOCIATION_ACCOUNTS,
              permissions.PERMISSION_ACCOUNTING_SETTINGS_UNIT_LEDGERS,
              permissions.PERMISSION_ACCOUNTING_SETTINGS_BANK_WITHDRAWALS,
              permissions.PERMISSION_ACCOUNTING_SETTINGS_SPLIT_RECEIPTS,
              permissions.PERMISSION_ACCOUNTING_SETTINGS_FINANCIAL_CLOSING,
              permissions.PERMISSION_ACCOUNTING_SETTINGS_CC_EMAIL,
            ]);
          },
        },
        {
          id: 'TOOLS',
          title: 'Tools',
          type: 'basic',
          link: '/accounting/tools',
          hidden: () => {
            return !this.permissionService.canany([
              permissions.PERMISSION_ACCOUNTING_TOOLS_SPLIT_RECEIPTS,
              permissions.PERMISSION_REPORT_UNIT_BALANCES_COMPARISON,
              permissions.PERMISSION_ACCOUNTING_TOOLS_TRANSFER_RECEIVABLE_BALANCE,
            ]);
          },
        },
      ],
    },
    // 6. Company Books
    {
      id: 'COMPANY_BOOKS',
      title: 'Company Books',
      type: 'collapsable',
      icon: 'calculate',
      hidden: this.hideGroup,
      children: [
        {
          id: 'GENERAL_LEDGER',
          title: 'General Ledger',
          type: 'basic',
          link: '/company-books/ledgers/gl',
          hidden: () => {
            return this.permissionService.cannot(permissions.PERMISSION_COMPANY_BOOKS_GENERAL_LEDGER);
          },
        },
        {
          id: 'Invoices',
          title: 'Invoices',
          type: 'basic',
          link: '/company-books/invoices',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([permissions.PERMISSION_COMPANY_BOOKS_INVOICES_VIEW, permissions.PERMISSION_COMPANY_BOOKS_INVOICES_ADD]);
          },
        },
        {
          id: 'company-books-receipts',
          title: 'Receipts',
          type: 'basic',
          link: '/company-books/receipts',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([permissions.PERMISSION_COMPANY_BOOKS_RECEIPTS_VIEW, permissions.PERMISSION_COMPANY_BOOKS_RECEIPTS_ADD]);
          },
        },
        {
          id: 'bills',
          title: 'Bills',
          type: 'basic',
          link: '/company-books/bills',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([
              permissions.PERMISSION_COMPANY_BOOKS_BILLS_VIEW,
              permissions.PERMISSION_COMPANY_BOOKS_BILLS_ADD,
              permissions.PERMISSION_COMPANY_BOOKS_BILLS_PAY,
              permissions.PERMISSION_COMPANY_BOOKS_BILLS_CANCEL,
            ]);
          },
        },
        {
          id: 'BANKING',
          title: 'Banking',
          type: 'basic',
          link: '/company-books/banking',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_COMPANY_BOOKS_BANKING_VIEW);
          },
        },
        {
          id: 'company-books-reports',
          title: 'Reports',
          type: 'basic',
          link: '/company-books/reports',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([
              permissions.PERMISSION_COMPANY_BOOKS_REPORT_CASH_FLOW,
              permissions.PERMISSION_COMPANY_BOOKS_REPORT_BALANCE_SHEET,
              permissions.PERMISSION_COMPANY_BOOKS_REPORT_GENERAL_FUND_STATEMENT,
              permissions.PERMISSION_COMPANY_BOOKS_REPORT_RESERVED_FUND_STATEMENT,
              permissions.PERMISSION_COMPANY_BOOKS_REPORT_TRIAL_BALANCE,
              permissions.PERMISSION_COMPANY_BOOKS_REPORT_STATEMENT_OF_EQUITY,
              permissions.PERMISSION_COMPANY_BOOKS_REPORT_DELINQUENT_OWNERS,
              permissions.PERMISSION_COMPANY_BOOKS_REPORT_RECEIVABLES_ACCOUNTS_STATEMENT,
              permissions.PERMISSION_COMPANY_BOOKS_REPORT_UNIT_LEDGERS,
              permissions.PERMISSION_COMPANY_BOOKS_REPORT_TAXATION,
              permissions.PERMISSION_COMPANY_BOOKS_BANKING_VIEW,
              permissions.PERMISSION_COMPANY_BOOKS_COLLECTION_REPORT_VIEW,
              permissions.PERMISSION_COMPANY_BOOKS_GENERAL_LEDGER,
            ]);
          },
        },
      ],
    },
    // 7. H2H
    {
      id: 'h2h',
      title: 'H2H',
      type: 'collapsable',
      icon: 'account_balance',
      hidden: this.hideGroup,
      children: [
        {
          id: 'h2h-adcb',
          title: 'ADCB',
          type: 'basic',
          link: '/accounting/host-to-host/adcb',
          hidden: (item: FuseNavigationItem) => {
            return (
              !this.authService.profile()?.company?.adcb_h2h ||
              !this.permissionService.canany([
                permissions.PERMISSION_HOST_TO_HOST_ADCB_VIEW,
                permissions.PERMISSION_HOST_TO_HOST_ADCB_BANK_ACCOUNTS_SETTING,
                permissions.PERMISSION_HOST_TO_HOST_ADCB_PAY,
              ])
            );
          },
        },
      ],
    },
    // 8. Recovery
    {
      id: 'recovery',
      title: 'Recovery',
      type: 'collapsable',
      icon: 'sms',
      hidden: () => {
        return this.permissionService.cannot(permissions.PERMISSION_ACCOUNTING_RECOVERY_VIEW);
      },
      children: [
        {
          id: 'analysis',
          title: 'Analysis',
          type: 'basic',
          link: '/recovery/analysis',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_ANALYSIS_VIEW);
          },
        },
        {
          id: 'reminders',
          title: 'Reminders',
          type: 'basic',
          link: '/recovery/delinquents',
        },
        {
          id: 'legal-notices',
          title: 'Legal Notice',
          type: 'basic',
          link: '/legal-notice/all',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_LEGAL_NOTICE_VIEW);
          },
        },
        {
          id: 'rdc',
          title: 'RDC',
          type: 'basic',
          link: '/rdc',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_RDC_VIEW);
          },
        },
        {
          id: 'settings',
          title: 'Settings',
          type: 'basic',
          link: '/recovery/settings',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_RECOVERY_SETTINGS);
          },
        },
      ],
    },
    // 9. Violations
    {
      id: 'Violations',
      title: 'Violations',
      type: 'collapsable',
      icon: 'error',
      hidden: this.hideGroup,
      children: [
        {
          id: 'Notices',
          title: 'Notices',
          type: 'basic',
          link: '/violations/notices',
          hidden: () => {
            return this.permissionService.cannot(PERMISSION_VIOLATION_NOTICES_VIEW);
          },
        },
        {
          id: 'Settings',
          title: 'Settings',
          type: 'basic',
          link: '/violations/settings',
          hidden: () => {
            return !this.permissionService.canany([PERMISSION_VIOLATION_SEVERITIES_VIEW, PERMISSION_VIOLATION_REPORTERS_VIEW]);
          },
        },
      ],
    },
    // 10. Procurement
    {
      id: 'procurement',
      title: 'Procurement',
      type: 'collapsable',
      icon: 'business_center',
      hidden: () => {
        return !this.permissionService.canany([permissions.PERMISSION_RFP_VIEW, permissions.PERMISSION_WORKORDER_VIEW, permissions.PERMISSION_CONTRACT_VIEW]);
      },
      children: [
        {
          id: 'RFP',
          title: 'RFP(S)',
          type: 'basic',
          link: '/procurement/rfps',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_RFP_VIEW);
          },
        },
        {
          id: 'work_order',
          title: 'Work Orders',
          type: 'basic',
          link: '/procurement/work-orders',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_WORKORDER_VIEW);
          },
        },
        {
          id: 'ppms',
          title: 'PPMs',
          type: 'basic',
          link: '/procurement/ppms',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_WORKORDER_VIEW);
          },
        },
        {
          id: 'contracts',
          title: 'Contracts',
          type: 'basic',
          link: '/procurement/contracts',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_CONTRACT_VIEW);
          },
        },
        {
          id: 'communications',
          title: 'Messages',
          type: 'basic',
          link: '/procurement/communications/list',
          hidden: () => false,
        },
        {
          id: 'templates',
          title: 'Templates',
          type: 'basic',
          link: '/procurement/templates',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_TEMPLATES_VIEW);
          },
        },
        {
          id: 'sla-templates',
          title: 'SLA Templates',
          type: 'basic',
          link: '/procurement/sla-templates/list',
          hidden: () => {
            return this.permissionService.cannot(permissions.PERMISSION_SLA_TEMPLATE_VIEW);
          },
        },
        {
          id: 'settings',
          title: 'Settings',
          type: 'basic',
          link: '/procurement/settings',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([permissions.PERMISSION_APPROVE_EVALUATION_VIEW, permissions.PERMISSION_VENDOR_LIST_VIEW]);
          },
        },
      ],
    },
    // 11. Task
    {
      id: 'tasks',
      title: 'Task',
      type: 'collapsable',
      icon: 'task_alt',
      hidden: this.hideGroup,
      children: [
        {
          id: 'tasks-list',
          title: 'All Tasks',
          type: 'basic',
          link: '/tasks',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_TASKS_VIEW_ALL);
          },
        },
        {
          id: 'tasks-my',
          title: 'My Tasks',
          type: 'basic',
          link: '/profile/my-tasks',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_TASKS_VIEW);
          },
        },
        {
          id: 'tasks-categories',
          title: 'Tasks Categories',
          type: 'basic',
          link: '/tasks/categories',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([permissions.PERMISSION_TASKS_CATEGORY_VIEW, permissions.PERMISSION_TASKS_CATEGORY_ADD_UPDATE, permissions.PERMISSION_TASKS_CATEGORY_DELETE]);
          },
        },
      ],
    },
    // 12. Reports
    {
      id: 'reports',
      title: 'Reports',
      type: 'basic',
      icon: 'assessment',
      link: '/reports',
      hidden: () => {
        return !this.permissionService.canany([
          permissions.PERMISSION_REPORT_CASH_FLOW,
          permissions.PERMISSION_REPORT_BALANCE_SHEET,
          permissions.PERMISSION_REPORT_PROFIT_AND_LOSS,
          permissions.PERMISSION_REPORT_GENERAL_FUND_STATEMENT,
          permissions.PERMISSION_REPORT_RESERVED_FUND_STATEMENT,
          permissions.PERMISSION_REPORT_TRIAL_BALANCE,
          permissions.PERMISSION_REPORT_STATEMENT_OF_EQUITY,
          permissions.PERMISSION_REPORT_BUDGET_VS_ACTUALS,
          permissions.PERMISSION_REPORT_DELINQUENT_OWNERS,
          permissions.PERMISSION_REPORT_RECEIVABLES_ACCOUNTS_STATEMENT,
          permissions.PERMISSION_REPORT_PAYABLES_ACCOUNTS_STATEMENT,
          permissions.PERMISSION_REPORT_UNIT_LEDGERS,
          permissions.PERMISSION_REPORT_VENDOR_LEDGERS,
          permissions.PERMISSION_REPORT_PROVIDER_LEDGERS,
          permissions.PERMISSION_REPORT_UNIT_BALANCES,
          permissions.PERMISSION_REPORT_UNIT_SUMMARY,
          permissions.PERMISSION_COLLECTION_REPORT_VIEW,
          permissions.PERMISSION_MANAGEMENT_REPORT_VIEW,
          permissions.PERMISSION_REPORT_TAXATION,
          permissions.PERMISSION_REPORT_FILE_TAX_RETURN,
          permissions.PERMISSION_COMPLIANCE_REPORT_VIEW,
          permissions.PERMISSION_COMMITTMENTS_REPORT_VIEW,
          permissions.PERMISSION_ACTIVE_WO_SUMMARY_REPORTS_VIEW,
          permissions.PERMISSION_COMMUNITY_INFO_REPORT_VIEW,
        ]);
      },
    },
    // 13. Communication
    {
      id: 'communication',
      title: 'Communication',
      type: 'collapsable',
      icon: 'mail',
      hidden: this.hideGroup,
      children: [
        {
          id: 'emails',
          title: 'Emails',
          type: 'basic',
          link: '/communication/emails',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_COMMUNICATION_EMAILS_VIEW);
          },
        },
        {
          id: 'notices',
          title: 'Noticeboard',
          type: 'basic',
          link: '/communication/notices',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_NOTICE_VIEW);
          },
        },
        {
          id: 'SMS',
          title: 'SMS',
          type: 'basic',
          link: '/communication/sms',
          hidden: (item: FuseNavigationItem) => {
            return !this.authService.profile()?.company?.sms_gateway_enabled || this.permissionService.cannot(permissions.PERMISSION_COMMUNICATION_MESSAGES_VIEW);
          },
        },
        {
          id: 'Surveys',
          title: 'Surveys',
          type: 'basic',
          link: '/communication/surveys',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_SURVEY_VIEW);
          },
        },
        {
          id: 'templates',
          title: 'Templates',
          type: 'basic',
          link: '/communication/templates',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_COMMUNICATION_TEMPLATES_VIEW);
          },
        },
        {
          id: 'visitors',
          title: 'Visitors',
          type: 'basic',
          link: '/communication/visitors',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_COMMUNICATION_VISITORS_VIEW);
          },
        },
      ],
    },
    // 14. Inspections
    {
      id: 'inspections',
      title: 'Inspections',
      type: 'collapsable',
      icon: 'list_alt',
      hidden: (group: FuseNavigationItem) => {
        return !this.authService.profile()?.company?.is_inspections_enabled || this.hideGroup(group);
      },
      children: [
        {
          id: 'inspection-reports',
          title: 'Reports',
          type: 'basic',
          link: '/inspections/reports',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_INSPECTION_REPORTS_VIEW);
          },
        },
        {
          id: 'inspection-snags',
          title: 'Snags',
          type: 'basic',
          link: '/inspections/snags',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_INSPECTION_SNAGS_VIEW);
          },
        },
        {
          id: 'inspection-settings',
          title: 'Settings',
          type: 'basic',
          link: '/inspections/settings',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([
              permissions.PERMISSION_INSPECTION_TEMPLATES_VIEW,
              permissions.PERMISSION_INSPECTION_INSPECTORS_VIEW,
              permissions.PERMISSION_INSPECTION_ROLES_VIEW,
              permissions.PERMISSION_SNAGS_CATEGORIES_VIEW,
              permissions.PERMISSION_INSPECTION_SNAGS_RESOLUTION_VIEW,
            ]);
          },
        },
      ],
    },
    // 15. Image Gallery
    {
      id: 'gallery',
      title: 'Image Gallery',
      type: 'basic',
      icon: 'photo_library',
      link: '/admin/gallery',
      hidden: () => {
        return this.permissionService.cannot(permissions.PERMISSION_GALLERY_VIEW_ADD);
      },
    },
    // 16. Directories
    {
      id: 'directories',
      title: 'Directories',
      type: 'collapsable',
      icon: 'book',
      hidden: this.hideGroup,
      children: [
        {
          id: 'vendors',
          title: 'Vendors',
          type: 'basic',
          link: '/directories/vendors',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([permissions.PERMISSION_VENDORS_VIEW, permissions.PERMISSION_VENDORS_ADD]);
          },
        },
        {
          id: 'Utility Providers',
          title: 'Utility Providers',
          type: 'basic',
          link: '/directories/providers',
          hidden: (item: FuseNavigationItem) => {
            return !this.permissionService.canany([permissions.PERMISSION_PROVIDERS_VIEW, permissions.PERMISSION_PROVIDERS_ADD]);
          },
        },
        {
          id: 'Owners',
          title: 'Owners',
          type: 'basic',
          link: '/directories/owners',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_OWNERS_VIEW);
          },
        },
        {
          id: 'Occupants',
          title: 'Occupants',
          type: 'basic',
          link: '/directories/residents',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_RESIDENTS_VIEW);
          },
        },
        {
          id: 'Representatives',
          title: 'Representatives',
          type: 'basic',
          link: '/directories/representatives',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_REPRESENTATIVES_VIEW);
          },
        },
        {
          id: 'Customers',
          title: 'Customers',
          type: 'basic',
          link: '/directories/customers',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_CUSTOMERS_VIEW);
          },
        },
        {
          id: 'Lease Companies',
          title: 'Lease Companies',
          type: 'basic',
          link: '/directories/lease-companies',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_LEASE_COMPANIES_VIEW);
          },
        },
        {
          id: 'Developers',
          title: 'Developers',
          type: 'basic',
          link: '/directories/developers',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_DEVELOPERS_VIEW);
          },
        },
      ],
    },
    // 17. Compliances
    {
      id: 'Compliances',
      title: 'Compliances',
      type: 'basic',
      icon: 'inventory',
      link: '/compliances',
      hidden: () => {
        return !this.permissionService.canany([permissions.PERMISSION_COMPLIANCE_LABEL_VIEW, permissions.PERMISSION_COMPANY_COMPLIANCE_VIEW, permissions.PERMISSION_ASSOCIATION_UNIT_COMPLIANCE_VIEW]);
      },
    },
    // 18. Insurances
    {
      id: 'insurance',
      title: 'Insurances',
      type: 'basic',
      icon: 'dashboard',
      link: '/insurances',
      hidden: () => {
        return this.permissionService.cannot(permissions.PERMISSION_INSURANCE_VIEW);
      },
    },
    // 19. Admin
    {
      id: 'admin',
      title: 'Admin',
      type: 'collapsable',
      icon: 'admin_panel_settings',
      hidden: this.hideGroup,
      children: [
        {
          id: 'company details',
          title: 'Company Details',
          type: 'basic',
          link: '/admin/company/details',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_ORGANIZATION_UPDATE);
          },
        },
        {
          id: 'roles',
          title: 'User Roles',
          type: 'basic',
          link: '/admin/roles',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_ROLES_VIEW);
          },
        },
        {
          id: 'users',
          title: 'Users',
          type: 'basic',
          link: '/admin/users',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_USERS_VIEW);
          },
        },
        {
          id: 'payment gateway',
          title: 'Payment Gateway',
          type: 'basic',
          link: '/admin/payment-gateway',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_GATEWAY_VIEW);
          },
        },
        {
          id: 'website settings',
          title: 'E-Services Portal',
          type: 'basic',
          link: '/admin/e-services',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_WEBSITE_SETTINGS);
          },
        },
        {
          id: 'call center',
          title: 'Call Center Portal',
          type: 'basic',
          link: '/admin/call-center',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_WEBSITE_SETTINGS);
          },
        },
        {
          id: 'user access',
          title: 'User Access Logs',
          type: 'basic',
          link: '/admin/user-access',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_ACCESS_LOGS_VIEW);
          },
        },
        {
          id: 'user activities',
          title: 'User Activity Logs',
          type: 'basic',
          link: '/activities',
          hidden: (item: FuseNavigationItem) => {
            return this.permissionService.cannot(permissions.PERMISSION_ACTIVITY_LOGS_OTHER);
          },
        },
      ],
    },
    // 20. Standards (dev-only, keep at end)
    // {
    //   id: 'standards',
    //   title: 'Standards',
    //   type: 'collapsable',
    //   icon: 'settings',
    //   hidden: this.hideGroup,
    //   children: [
    //     {
    //       id: 'standards-spaced',
    //       title: 'Spaced Page',
    //       type: 'basic',
    //       link: '/standards/spaced-page',
    //       hidden: (item: FuseNavigationItem) => {
    //         return false;
    //       },
    //     },
    //     {
    //       id: 'standards-full',
    //       title: 'Full Page',
    //       type: 'basic',
    //       link: '/standards/full-page',
    //     },
    //   ],
    // },
  ];

  private hideGroup(group: FuseNavigationItem): boolean {
    return (group.children ?? []).every((item) => {
      if (item.hidden) {
        return item.hidden(item);
      }
      return false;
    });
  }
}
